import { skipToken } from "@reduxjs/toolkit/query";
import { Color } from "@repo/app-types/constants/colors";
import { checkIsManagerGroup } from "@repo/app-types/entities/group";
import {
  asTeammateId,
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import type {
  PredefinedTeammateRoleType,
  TeammateRoleType,
} from "@repo/app-types/entities/teammatePermissions";
import {
  allTeammateRoleTypes,
  isPredefinedTeammateRoleType,
  PermissionActions,
  PermissionSubjects,
  predefinedRoleNameByType,
  TeammateRoleTypes,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { isPerformanceCompany } from "@repo/app-types/helpers/billingFeatureGating";
import { isAlpharunEmail } from "@repo/app-types/helpers/utilities";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { SmallTabs } from "@repo/ui/Navigation/smallTabs";
import { Pill } from "@repo/ui/Pills/pill";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { EntityTable } from "@repo/ui/tables/entityTable";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconBan, IconSearch, IconTrash } from "@tabler/icons-react";
import { DateTime } from "luxon";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import { useLicenseUtilizationQuery } from "../../../api/entities/company.js";
import {
  useDeleteCompanyInvitationMutation,
  usePendingInvitationsPaginatedInfiniteQuery,
} from "../../../api/entities/companyInvitation.js";
import {
  useAllTeammateGroupsQuery,
  useSetTeammateManagerMutation,
} from "../../../api/entities/group.js";
import {
  useTeammatesPaginatedInfiniteQuery,
  useUpdateTeammateMutation,
} from "../../../api/entities/teammate.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  selectCompany,
  selectIsRecruitingModeCompany,
} from "../../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { selectTeammate } from "../../teammate/teammateSlice.js";
import {
  teammateRemoved,
  teammateUpdated,
} from "../../teammates/teammatesSlice.js";
import { InviteTeammatesSection } from "./inviteTeammatesSection.js";
import { TeammateManagerCell } from "./teammateManagerCell.js";

const roleOptions = allTeammateRoleTypes
  .filter(isPredefinedTeammateRoleType)
  .map((role) => ({
    label: predefinedRoleNameByType[role],
    value: role,
  }));

interface InvitationRow {
  id: string;
  email: string;
  role: TeammateRoleType;
  dateAdded: string;
}

interface TeammateRow {
  id: string;
  email: string;
  name: string;
  role: TeammateRoleType;
  managerId: TeammateId | null | undefined;
  managerDisplayName: string | undefined;
  dateAdded: string;
}

// Plain-language summary of what each predefined role can do in the performance
// product. Shown to performance customers on the teammates settings page so
// admins understand the differences before assigning roles.
const performanceRoleDescriptions: readonly {
  role: PredefinedTeammateRoleType;
  description: string;
}[] = [
  {
    role: TeammateRoleTypes.EDITOR,
    description:
      "Can build and edit playbooks, configure settings, manage teammates and view all data.",
  },
  {
    role: TeammateRoleTypes.REVIEWER,
    description:
      "Can view dashboards, calls, and playbooks, but can't edit playbooks and company settings.",
  },
  {
    role: TeammateRoleTypes.TEAM_MEMBER,
    description:
      "Can view calls and their own performance stats, but can't see team stats and leaderboards.",
  },
];

const PerformanceRolesExplainer: FC<{ showTeamMember: boolean }> = ({
  showTeamMember,
}) => (
  <div className="mt-6">
    <dl className="space-y-3">
      {performanceRoleDescriptions
        .filter(
          ({ role }) =>
            showTeamMember || role !== TeammateRoleTypes.TEAM_MEMBER,
        )
        .map(({ role, description }) => (
          <div
            key={role}
            className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3"
          >
            <dt className="sm:w-28 sm:shrink-0">
              <Pill
                label={predefinedRoleNameByType[role]}
                size={Size.SMALL}
                color={Color.Gray}
              />
            </dt>
            <dd className="max-w-2xl text-sm text-muted-foreground">
              {description}
            </dd>
          </div>
        ))}
    </dl>
  </div>
);

export const TeammatesTab: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const currentTeammate = useAppSelector(selectTeammate);
  const isRecruitingModeCompany = useAppSelector(selectIsRecruitingModeCompany);

  const isPerformance = company ? isPerformanceCompany(company) : false;
  const { data: licenseUtilization, isLoading: isLicenseUtilizationLoading } =
    useLicenseUtilizationQuery(isPerformance ? undefined : skipToken);

  const hasTeamPerformanceTab = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );
  const hasGroups = hasTeamPerformanceTab;

  const [deleteCompanyInvitation] = useDeleteCompanyInvitationMutation();
  const [updateTeammate] = useUpdateTeammateMutation();
  const [setTeammateManager] = useSetTeammateManagerMutation();

  const { data: groups } = useAllTeammateGroupsQuery(
    hasGroups ? undefined : skipToken,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const [selectedSubTabIndex, setSelectedSubTabIndex] = useState(0);

  const [invitationToDelete, setInvitationToDelete] = useState<string | null>(
    null,
  );
  const [teammateToDeactivate, setTeammateToDeactivate] =
    useState<TeammateId | null>(null);

  // Paginated invitations query
  const {
    data: invitationsData,
    isLoading: isInvitationsLoading,
    isError: isInvitationsError,
    hasNextPage: hasNextInvitationsPage,
    fetchNextPage: fetchNextInvitationsPage,
    isFetchingNextPage: isFetchingNextInvitationsPage,
    refetch: refetchInvitations,
  } = usePendingInvitationsPaginatedInfiniteQuery({
    search: debouncedSearch || undefined,
  });

  // Paginated teammates query
  const {
    data: teammatesData,
    isLoading: isTeammatesLoading,
    isError: isTeammatesError,
    hasNextPage: hasNextTeammatesPage,
    fetchNextPage: fetchNextTeammatesPage,
    isFetchingNextPage: isFetchingNextTeammatesPage,
    refetch: refetchTeammates,
  } = useTeammatesPaginatedInfiniteQuery({
    search: debouncedSearch || undefined,
  });

  // Hide internal Alpharun accounts from customers; only show them in our own
  // workspaces.
  const isAlpharunWorkspace =
    company?.name.toLowerCase().includes("alpharun") ?? false;

  const invitations = useMemo(
    () => invitationsData?.pages.flatMap((page) => page.invitations) ?? [],
    [invitationsData],
  );

  const teammates = useMemo(() => {
    const allTeammates =
      teammatesData?.pages.flatMap((page) => page.teammates) ?? [];
    return isAlpharunWorkspace
      ? allTeammates
      : allTeammates.filter((t) => !isAlpharunEmail(t.email));
  }, [teammatesData, isAlpharunWorkspace]);

  // Build invitation rows
  const invitationRows: readonly InvitationRow[] = useMemo(
    () =>
      invitations.map((inv) => ({
        id: inv.id,
        email: inv.recipient_email,
        role: inv.metadata.permissions.role.type,
        dateAdded: inv.created_at,
      })),
    [invitations],
  );

  // Build manager lookup
  const getManagerId = useMemo(() => {
    if (!groups) {
      return () => undefined;
    }

    const map = new Map<TeammateId, TeammateId>();

    for (const group of groups) {
      if (!checkIsManagerGroup(group)) {
        continue;
      }

      for (const teammateId of group.teammate_ids) {
        map.set(teammateId, group.manager_id);
      }
    }

    return (teammateId: TeammateId) => map.get(teammateId) ?? null;
  }, [groups]);

  const teammateDisplayNameById = useMemo(() => {
    const map = new Map<TeammateId, string>();
    for (const t of teammates) {
      const { primaryDisplayName, secondaryDisplayName } =
        buildTeammateDisplayNames(t);
      map.set(t.id, secondaryDisplayName ?? primaryDisplayName);
    }
    return map;
  }, [teammates]);

  // Build teammate rows
  const teammateRows: readonly TeammateRow[] = useMemo(() => {
    return teammates.map((t) => {
      const managerId = getManagerId(t.id);
      return {
        id: t.id,
        email: t.email,
        name: buildTeammateDisplayNames(t).primaryDisplayName,
        role: t.permissions.role.type,
        managerId,
        managerDisplayName: managerId
          ? teammateDisplayNameById.get(managerId)
          : undefined,
        dateAdded: t.created_at,
      };
    });
  }, [teammates, getManagerId, teammateDisplayNameById]);

  const filteredRoleOptions = useMemo(
    () =>
      roleOptions.filter((role) => {
        if (role.value === TeammateRoleTypes.TEAM_MEMBER) {
          return hasTeamPerformanceTab;
        }
        return true;
      }),
    [hasTeamPerformanceTab],
  );

  // Action handlers
  const onDeleteInvitation = async (invitationId: string) => {
    try {
      await deleteCompanyInvitation(invitationId).unwrap();
      showNotification(dispatch, {
        id: `invitation-deleted-${invitationId}`,
        primaryMessage: "Invitation cancelled",
        type: NotificationType.SUCCESS,
      });
      void refetchInvitations();
    } catch {
      showNotification(dispatch, {
        id: `invitation-delete-failed-${invitationId}`,
        primaryMessage: "Failed to cancel invitation",
        type: NotificationType.FAILURE,
      });
    }
    setInvitationToDelete(null);
  };

  const deactivateTeammate = async (teammateId: TeammateId) => {
    const notificationId = uuid();
    try {
      await updateTeammate({
        id: teammateId,
        patchPayload: { is_active: false },
      }).unwrap();
      dispatch(teammateRemoved(teammateId));
      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: "Teammate deactivated",
        type: NotificationType.SUCCESS,
      });
      void refetchTeammates();
    } catch {
      showNotification(dispatch, {
        id: notificationId,
        primaryMessage: "Teammate deactivation failed",
        type: NotificationType.FAILURE,
      });
    }
    setTeammateToDeactivate(null);
  };

  const updateTeammateRole = useCallback(
    async (teammateId: TeammateId, role: PredefinedTeammateRoleType) => {
      const notificationId = uuid();
      try {
        const updatedTeammateResult = await updateTeammate({
          id: teammateId,
          patchPayload: { permissions: { role: { type: role } } },
        }).unwrap();
        dispatch(
          teammateUpdated({ id: teammateId, changes: updatedTeammateResult }),
        );
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate role updated",
          type: NotificationType.SUCCESS,
        });
      } catch {
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate role update failed",
          type: NotificationType.FAILURE,
        });
      }
    },
    [dispatch, updateTeammate],
  );

  const updateTeammateManager = useCallback(
    async (teammateId: TeammateId, managerId: TeammateId | null) => {
      const notificationId = uuid();
      try {
        await setTeammateManager({ teammateId, managerId }).unwrap();
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate manager updated",
          type: NotificationType.SUCCESS,
        });
      } catch {
        showNotification(dispatch, {
          id: notificationId,
          primaryMessage: "Teammate manager update failed",
          type: NotificationType.FAILURE,
        });
      }
    },
    [dispatch, setTeammateManager],
  );

  // Invitation table columns
  const invitationColumns: readonly EntityTableColumnDefinition<InvitationRow>[] =
    useMemo(
      () => [
        {
          id: "email",
          header: "Email",
          isRowHeader: true,
          renderCell: (row) => (
            <span className="text-sm text-foreground">{row.email}</span>
          ),
        },
        {
          id: "role",
          header: "Role",
          className: "w-0",
          renderCell: (row) => (
            <Pill
              label={
                isPredefinedTeammateRoleType(row.role)
                  ? predefinedRoleNameByType[row.role]
                  : row.role
              }
              size={Size.SMALL}
              color={Color.Gray}
            />
          ),
        },
        {
          id: "dateAdded",
          header: "Invited",
          className: "w-0",
          renderCell: (row) => (
            <span className="text-sm text-muted-foreground">
              {DateTime.fromISO(row.dateAdded).toFormat("MMM dd yyyy")}
            </span>
          ),
        },
        {
          id: "actions",
          header: "",
          className: "w-0",
          renderCell: (row) => (
            <div className="flex justify-end">
              <TooltipTrigger>
                <IconButton
                  variant={ButtonVariant.Secondary}
                  icon={<IconTrash className="h-4 w-4" />}
                  onClick={() => setInvitationToDelete(row.id)}
                  accessibleLabel="Cancel invitation"
                />
                <SimpleTooltip>Cancel invitation</SimpleTooltip>
              </TooltipTrigger>
            </div>
          ),
        },
      ],
      [],
    );

  // Teammate table columns
  const teammateColumns: readonly EntityTableColumnDefinition<TeammateRow>[] =
    useMemo(
      () => [
        {
          id: "name",
          header: "Name",
          isRowHeader: true,
          renderCell: (row) => (
            <div>
              <div className="text-sm font-medium text-foreground">
                {row.name}
              </div>
              <div className="text-sm text-muted-foreground">{row.email}</div>
            </div>
          ),
        },
        ...(isRecruitingModeCompany && hasGroups
          ? [
              {
                id: "manager",
                header: "Manager",
                className: "w-0",
                renderCell: (row: TeammateRow) => (
                  <Can
                    I={PermissionActions.UPDATE}
                    a={PermissionSubjects.TEAMMATES}
                    passThrough={true}
                  >
                    {(allowed) => (
                      <TeammateManagerCell
                        managerId={row.managerId ?? null}
                        managerDisplayName={row.managerDisplayName}
                        isDisabled={!groups || !allowed}
                        onChange={(managerId) => {
                          void updateTeammateManager(
                            asTeammateId(row.id),
                            managerId,
                          );
                        }}
                      />
                    )}
                  </Can>
                ),
              } satisfies EntityTableColumnDefinition<TeammateRow>,
            ]
          : []),
        ...(isRecruitingModeCompany
          ? [
              {
                id: "role",
                header: "Role",
                className: "w-0",
                renderCell: (row: TeammateRow) => (
                  <Can
                    I={PermissionActions.UPDATE}
                    a={PermissionSubjects.TEAMMATES}
                    passThrough={true}
                  >
                    {(allowed) => (
                      <SimpleSelect
                        label="Role"
                        isLabelHidden
                        value={row.role}
                        onChange={(option) => {
                          const changedRole =
                            option as PredefinedTeammateRoleType;
                          if (changedRole !== row.role) {
                            void updateTeammateRole(
                              asTeammateId(row.id),
                              changedRole,
                            );
                          }
                        }}
                        options={filteredRoleOptions}
                        isDisabled={!allowed || row.id === currentTeammate?.id}
                      />
                    )}
                  </Can>
                ),
              } satisfies EntityTableColumnDefinition<TeammateRow>,
            ]
          : []),
        {
          id: "dateAdded",
          header: "Date added",
          className: "w-0",
          renderCell: (row) => (
            <span className="text-sm text-muted-foreground">
              {DateTime.fromISO(row.dateAdded).toFormat("MMM dd yyyy")}
            </span>
          ),
        },
        {
          id: "actions",
          header: "",
          className: "w-0",
          renderCell: (row) =>
            row.id !== currentTeammate?.id ? (
              <div className="flex justify-end">
                <TooltipTrigger>
                  <IconButton
                    variant={ButtonVariant.Secondary}
                    icon={<IconBan className="h-4 w-4" />}
                    onClick={() =>
                      setTeammateToDeactivate(asTeammateId(row.id))
                    }
                    accessibleLabel="Deactivate teammate"
                  />
                  <SimpleTooltip>Deactivate teammate</SimpleTooltip>
                </TooltipTrigger>
              </div>
            ) : null,
        },
      ],
      [
        isRecruitingModeCompany,
        hasGroups,
        groups,
        filteredRoleOptions,
        currentTeammate?.id,
        updateTeammateManager,
        updateTeammateRole,
      ],
    );

  return (
    <>
      <div className="border-b border-gray-900/10 py-4">
        <h2 className="pb-4 text-lg leading-6 font-semibold text-foreground">
          Invite teammates
        </h2>
        <InviteTeammatesSection onInviteTeammates={refetchInvitations} />
        {isPerformance && (
          <PerformanceRolesExplainer showTeamMember={hasGroups} />
        )}
      </div>

      {isPerformance ? (
        isLicenseUtilizationLoading ? (
          <div className="py-4">
            <div className="h-6 w-48 animate-pulse rounded-sm bg-gray-200" />
          </div>
        ) : licenseUtilization ? (
          <div className="py-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                License utilization:
              </span>
              <Pill
                label={
                  licenseUtilization.max_licenses !== null
                    ? `${licenseUtilization.used_licenses} / ${licenseUtilization.max_licenses}`
                    : `${licenseUtilization.used_licenses}`
                }
                size={Size.SMALL}
                color={
                  licenseUtilization.max_licenses !== null &&
                  licenseUtilization.used_licenses >
                    licenseUtilization.max_licenses
                    ? Color.Amber
                    : Color.Gray
                }
              />
            </div>
            {licenseUtilization.max_licenses !== null &&
              licenseUtilization.used_licenses >
                licenseUtilization.max_licenses && (
                <div className="mt-2 text-sm text-amber-600">
                  You&apos;re using more licenses than your plan includes.
                  Additional licenses are billed at the start of each month, or
                  in accordance with your contract.
                </div>
              )}
          </div>
        ) : null
      ) : (
        <div className="py-4 text-sm text-muted-foreground">
          Your plan includes unlimited teammates.
        </div>
      )}

      <div className="py-4">
        <div className="mb-4 max-w-xs">
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            leftIcon={<IconSearch className="h-4 w-4 text-gray-400" />}
            maxLength={100}
          />
        </div>

        <div className="mb-4">
          <SmallTabs
            tabs={["Active teammates", "Pending invitations"]}
            selectedTabIndex={selectedSubTabIndex}
            onSelectTabIndex={setSelectedSubTabIndex}
          />
        </div>

        <div className="overflow-x-auto">
          {selectedSubTabIndex === 0 ? (
            <EntityTable
              aria-label="Active teammates"
              columns={teammateColumns}
              entities={teammateRows}
              isLoading={isTeammatesLoading}
              isError={isTeammatesError}
              onLoadMore={
                hasNextTeammatesPage && !isFetchingNextTeammatesPage
                  ? fetchNextTeammatesPage
                  : undefined
              }
              emptyMessage="No teammates found."
            />
          ) : (
            <EntityTable
              aria-label="Pending invitations"
              columns={invitationColumns}
              entities={invitationRows}
              isLoading={isInvitationsLoading}
              isError={isInvitationsError}
              onLoadMore={
                hasNextInvitationsPage && !isFetchingNextInvitationsPage
                  ? fetchNextInvitationsPage
                  : undefined
              }
              emptyMessage="No pending invitations."
            />
          )}
        </div>
      </div>

      <SimpleModal
        isOpen={Boolean(invitationToDelete)}
        onCancel={() => setInvitationToDelete(null)}
        variant={SimpleModalVariant.Warning}
        title="Revoke invitation"
        subtitle="Are you sure you want to cancel this invitation?"
        confirmButtonText="Cancel invitation"
        onConfirm={() => {
          if (invitationToDelete) {
            void onDeleteInvitation(invitationToDelete);
          }
        }}
      />
      <SimpleModal
        isOpen={Boolean(teammateToDeactivate)}
        onCancel={() => setTeammateToDeactivate(null)}
        variant={SimpleModalVariant.Warning}
        title="Deactivate teammate"
        subtitle="Deactivating this teammate will prevent them from logging in. They won't count toward your license usage. You can reactivate them by re-inviting them."
        confirmButtonText="Deactivate teammate"
        onConfirm={() => {
          if (teammateToDeactivate) {
            void deactivateTeammate(teammateToDeactivate);
          }
        }}
      />
    </>
  );
};
